// InviteJoin.js
import { useEffect, useRef, useState } from "react";

export default function InviteJoin({
  socket,
  chatId,
  userName,
  inviteToken,
  onSuccess,
  onFailure,
}) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);

  // 🔒 HARD GUARD — survives re-renders & StrictMode
  const previewRequestedRef = useRef(false);

  const inviteLink = `${window.location.origin}/invite/${inviteToken}`;

  /* =====================================================
     🔍 PREVIEW GROUP — ONCE ONLY
  ===================================================== */
  useEffect(() => {
    if (!socket || !inviteToken) return;
    if (previewRequestedRef.current) return;
    previewRequestedRef.current = true;

    socket.emit("group-preview-by-invite", { link: inviteLink });

    const onSuccessPreview = (group) => {
      setPreview(group);
      setLoading(false);
    };

    const onFailedPreview = (err) => {
      setLoading(false);
      setPreview(null);
      onFailure?.(err);
    };

    socket.once("group-preview-success", onSuccessPreview);
    socket.once("group-preview-failed", onFailedPreview);

    return () => {
      socket.off("group-preview-success", onSuccessPreview);
      socket.off("group-preview-failed", onFailedPreview);
    };
  }, [socket, inviteToken]); // ❌ DO NOT add inviteLink / onFailure

  /* =====================================================
     ✅ JOIN GROUP — USER ACTION ONLY
  ===================================================== */
  const handleJoin = () => {
    if (!socket || !chatId || !userName || joining) return;
    setJoining(true);

    socket.emit("group-join-via-link", {
      link: inviteLink,
      chatId,
      userName,
    });

    socket.once("group-join-success", (group) => {
      setJoining(false);
      onSuccess?.(group);
    });

    socket.once("group-join-failed", (err) => {
      setJoining(false);
      onFailure?.(err);
    });
  };

  /* =====================================================
     ❌ CANCEL — SILENT EXIT
  ===================================================== */
  const handleCancel = () => {
    sessionStorage.removeItem("pendingInviteToken");
  };

  /* =====================================================
     🖼️ UI
  ===================================================== */
  return (
    <div className="flex flex-col items-center justify-center h-full px-6 text-center gap-4">
      {loading ? (
        <div className="text-sm text-neutral-500">Loading group…</div>
      ) : preview ? (
        <>
          {preview.groupImageUrl ? (
            <img
              src={preview.groupImageUrl}
              alt="Group avatar"
              className="h-24 w-24 rounded-full object-cover border"
            />
          ) : (
            <div className="h-24 w-24 rounded-full flex items-center justify-center bg-neutral-200 text-4xl">
              👥
            </div>
          )}

          {/* ✅ FIX: was broken as [preview.name](http://preview.name) */}
          <div className="text-xl font-semibold">{preview.name}</div>

          <div className="text-sm text-neutral-500">
            {preview.membersCount} members
          </div>

          <div className="flex gap-3 mt-4">
            <button
              onClick={handleJoin}
              disabled={joining}
              className="px-6 py-2 rounded-full bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {joining ? "Joining…" : "Join Group"}
            </button>
            <button
              onClick={handleCancel}
              className="px-6 py-2 rounded-full border border-neutral-300 text-neutral-700 hover:bg-neutral-100"
            >
              Cancel
            </button>
          </div>
        </>
      ) : (
        <div className="text-sm text-red-500">
          Invalid or expired invite link
        </div>
      )}
    </div>
  );
}